import { DateFormat, formatDate } from "@nova/format"
import * as Icons from "@nova/component-icons"
import { PDFJob } from "@nova/protobuf"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectWorkspaceMembers } from "features/entity-caching/members/UserManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { throttle } from "nodash"
import type { PluginSpec } from "plugins/plugin-registry"
import { FC, useCallback, useMemo } from "react"
import { downloadUri } from "tools/browser/download"
import { RenamableLabel } from "@nova/component-renamable-label"
import classNames from "classnames"
import { selectRenamingLayerId, setRenamingLayerId } from "../slice.new"
import Image from "next/image"
import { useLazyLoadedImage } from "@nova/toolkit-pure-hooks"
import { setActiveImageId } from "features/map/layers/slice"
import { ImagePreviewType, JobStatusType } from "@nova/constants"
import { getGenericListItemProgress } from "./GenericLayerListItemProgress"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"

export function getGenericPdfListItem({
    spec,
    ProgressComponent = !!spec.useUploadStatusAndProgress
        ? getGenericListItemProgress({
              useStatusAndProgress: spec.useUploadStatusAndProgress,
              overrideProcessingText: "Generating",
          })
        : undefined,
}: {
    spec: PluginSpec<PDFJob>
    ProgressComponent?: FC<{ id: string; isVisible: boolean }>
}) {
    const GenericPdfListItem = ({ id, isHovered }: { id: string; isHovered: boolean }) => {
        const dispatch = useAppDispatch()
        const isEditor = useIsEditor()
        const entity = useAppSelector(spec.selectSingleEntity(id))
        const hasCompleted =
            entity.status === JobStatusType.COMPLETED ||
            entity.status === JobStatusType.MOCKCOMPLETED
        const hasPreview = hasCompleted && !!entity.pages
        const renamingLayerId = useAppSelector(selectRenamingLayerId)
        const isRenaming = renamingLayerId === id
        const workspaceMembers = useAppSelector(selectWorkspaceMembers)
        const creatorFullName = useMemo(() => {
            const creator = workspaceMembers.find(
                (member) => member.userId === entity.createdBy?.userId
            )
            return creator?.fullName || "Unknown Creator"
        }, [entity.createdBy?.userId, workspaceMembers])
        const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
        const downloadPdf = useMemo(
            () =>
                throttle(
                    (portfolioId: string, id: string, name: string) =>
                        downloadUri(
                            `${name}.pdf`,
                            `/api/organization/${portfolioId}/job/${id}/map-direct.pdf`
                        ),
                    2000
                ),
            []
        )
        const fetchBlob = useCallback(
            () =>
                fetch(`/api/organization/${workspaceId}/job/${id}/preview.png?page=0&lr=true`).then(
                    (res) => res.blob()
                ),
            [workspaceId, id]
        )
        const { blobUrl, isFetching, containerRef, isError } = useLazyLoadedImage({
            type: "PDFThumbnail",
            imageKey: id,
            fetchBlob,
            dependencies: [hasPreview],
        })
        return (
            <div
                className="w-full flex flex-col justify-center items-center relative p-2 gap-2"
                data-drag-type="touch"
                onClick={(e) => {
                    if (hasPreview && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                        dispatch(
                            setActiveImageId({
                                ids: { [id]: true },
                                type: ImagePreviewType.PDF,
                            })
                        )
                    }
                }}
                onDoubleClick={() => {
                    if (!isEditor) return
                    dispatch(setRenamingLayerId({ id }))
                }}
            >
                {hasPreview && (
                    <div
                        ref={containerRef}
                        className="relative w-full aspect-[4/3] max-h-[150px] rounded-lg overflow-hidden bg-[#1D1D21]" // Between gray-700 and gray-800
                    >
                        {!!blobUrl && !isFetching && !isError && (
                            <Image
                                src={blobUrl}
                                alt=""
                                fill
                                className={classNames("object-contain")}
                                priority
                                unoptimized
                                draggable={false}
                            />
                        )}
                        {isError && (
                            <div className="w-full h-full flex items-center justify-center">
                                <Icons.SmallIcon
                                    Icon={Icons.BrokenImage}
                                    className="text-white-300"
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className="w-full flex justify-between">
                    <div className="flex flex-col gap-2 shrink min-w-0">
                        <RenamableLabel
                            value={entity.name}
                            rename={isRenaming}
                            highlightOnRenameStart
                            setRename={
                                isRenaming
                                    ? (r) =>
                                          dispatch(setRenamingLayerId({ id: r ? id : undefined }))
                                    : () => null
                            }
                            classNames="w-max select-none"
                            inputClassNames="!max-h-5 !text-[12px]"
                            onRename={(name) => {
                                dispatch(spec.updateAction({ id, updates: { name } }))
                                dispatch(setRenamingLayerId({ id: undefined }))
                            }}
                            style={{
                                maxWidth: "100%",
                                minWidth: 10,
                            }}
                        />
                        {hasCompleted && (
                            <div className="flex gap-1">
                                <div>
                                    <div className="w-4 min-w-[16px] aspect-square rounded overflow-hidden">
                                        <ProfilePhoto
                                            userId={entity.createdBy?.userId || ""}
                                            fullName={creatorFullName}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-[2px]">
                                    <small className="text-gray-300">{creatorFullName}</small>
                                    <small className="text-gray-300">
                                        {formatDate({
                                            value: entity.createdAt * 1000,
                                            format: DateFormat.MMMDDYYYY,
                                            omitSuffix: true,
                                        })}
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                    {hasCompleted && (
                        <div>
                            {isHovered && !isRenaming && (
                                <button
                                    type="button"
                                    className="group"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        downloadPdf(workspaceId, entity.id, entity.name)
                                    }}
                                >
                                    <Icons.ExtraSmallIcon
                                        Icon={Icons.Download}
                                        className="text-gray-200 group-hover:text-white"
                                    />
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {!!ProgressComponent && (
                    <div className="w-full">
                        <ProgressComponent id={id} isVisible />
                    </div>
                )}
            </div>
        )
    }
    return GenericPdfListItem
}
